import { WebSocketMessage } from '../../context/AzureWebSocketContext'
import { Buffer } from 'buffer'
import useProcessErrorNotification, { ErrorNotification } from './useProcessErrorNotification'
import useProcessTimeTrackingNotification, { TimeTrackingNotification } from './useProcessTimeTrackingNotification'
import useProcessDocumentNotification, { DocumentNotification } from './useProcessDocumentNotification'
import useProcessCancelTimeTrackingNotification, { CancelTimeTrackingNotification } from './useProcessCancelTimeTrackingNotification'
import useProcessEmailFiledNotification, { EmailFiledNotification } from './useProcessEmailFiledNotification'
import useProcessEmailFilingSuggestNotification, { EmailFilingSuggestNotification } from './useProcessEmailFilingSuggestNotification'
import { appInsightsReact } from '../../pages/App'
import { DateTime } from 'luxon'
import useProcessFileUpsertNotification, { FileUpsertNotification } from './useProcessFileUpsertNotification'

/**
 * Defines all the different types of event messages the client might receive.
 */
export enum NotificationType {
    CancelTimeTracking = 'cancel-time-tracking',
    Document = 'document',
    DocumentV2 = 'document-v2',
    EmailFiled = 'email-filed',
    EmailFilingSuggest = 'email-filing-suggest',
    Error = 'error',
    FileUpsert = 'file-upsert',
    Invoice = 'invoice',
    PdfConversion = 'pdf-conversion',
    Report = 'report',
    TimeTracking = 'time-tracking',
    Zip = 'zip',
}

export type EventMessageData<DataT> = DataT & {
    type: NotificationType
}

// Provide an arbitrary state field to return messages that are in response of some user-defined action.
export type StatefulMessage<ParamsT> = ParamsT & {
    state?: string
}

export type UnprocessedMessage = WebSocketMessage<{
    sequenceId: number
    dataType: EventMessageDataType
    data: unknown
}>

/**
 * Message originating from one of the publishers.
 */
export type ProcessedMessage<DataT> = Omit<UnprocessedMessage, 'dataType' | 'data'> & {
    data: EventMessageData<DataT>
}

/**
 * Defines all the different data types a message can have.
 */
enum EventMessageDataType {
    Text = 'text',
    Json = 'json',
    Binary = 'binary',
}

function parseMessageData(message: UnprocessedMessage): EventMessageData<unknown> {
    switch (message.dataType) {
        case EventMessageDataType.Binary:
            return JSON.parse(Buffer.from(message.data as string, 'base64').toString('binary'))
        case EventMessageDataType.Text:
            return JSON.parse(message.data as string)
        case EventMessageDataType.Json:
        default:
            // No additional action needed, data is already in json format.
            return (message as ProcessedMessage<unknown>).data
    }
}

/**
 * Process custom event messages based on the type. Each type has its own data scheme,
 * so the data property is of type any.
 */
export default function useProcessMessage() {
    const processTimeTrackingNotification = useProcessTimeTrackingNotification()
    const processCancelTimeTrackingNotification = useProcessCancelTimeTrackingNotification()
    const processEmailFiledNotification = useProcessEmailFiledNotification()
    const processEmailFilingSuggestNotification = useProcessEmailFilingSuggestNotification()
    const processFileUpsertNotification = useProcessFileUpsertNotification()
    const processDocumentNotification = useProcessDocumentNotification()
    const processErrorMessage = useProcessErrorNotification()
    return (unprocessedMessage: UnprocessedMessage) => {
        const message: ProcessedMessage<unknown> = {
            ...unprocessedMessage,
            data: parseMessageData(unprocessedMessage),
        }

        appInsightsReact.trackEvent({
            name: 'useProcessMessage',
            properties: {
                ...message.data,
            },
        })

        console.log('Received pubsub message of type', message?.data?.type, ' at ', DateTime.now().toISO(), '.')

        switch (message.data.type) {
            case NotificationType.TimeTracking:
                return processTimeTrackingNotification(message as TimeTrackingNotification)
            case NotificationType.CancelTimeTracking:
                return processCancelTimeTrackingNotification(message as CancelTimeTrackingNotification)
            case NotificationType.EmailFiled:
                return processEmailFiledNotification(message as EmailFiledNotification)
            case NotificationType.EmailFilingSuggest:
                return processEmailFilingSuggestNotification(message as EmailFilingSuggestNotification)
            case NotificationType.FileUpsert:
                return processFileUpsertNotification(message as FileUpsertNotification)
            case NotificationType.Invoice:
            case NotificationType.Document:
            case NotificationType.DocumentV2:
            case NotificationType.Report:
            case NotificationType.PdfConversion:
            case NotificationType.Zip:
                return processDocumentNotification(message as DocumentNotification)
            case NotificationType.Error:
                return processErrorMessage(message.data as ErrorNotification)
        }
    }
}
